import * as React from 'react';

import type {ExternalService} from 'nutshell-graphql-types';
import type {
    ActivityCreateInput,
    GetPotentialActivityEntities_activityPotentialEntities_relatedEntities as RelatedEntity,
    GetPotentialActivityEntities_activityPotentialEntities_participants as Participant,
    GetCalendarSyncForActivityModal_loggedInUser_calendarSync as CalendarSync,
    GetActivityDefaults_activityDefaults_previousActivity as PreviousActivity,
} from 'nutshell-graphql-types';

import {checkMultiDay} from '../../../core/date-time/activity-time-helpers';

import type {Option} from '../core/select/graphql-select-inline';
import type {SelectOption as PickerSelectOption} from '../setup/import/dromo/types';

import {useGetActivityDefaults} from './graphql/hooks/use-get-activity-defaults';
import {useGetPotentialActivityEntities} from './graphql/hooks/use-get-potential-activity-entities';
import {getOrCreateLocalStorageShouldSyncToCalendar} from './helpers';
import {useCalendarState} from './tabs/calendar/hooks';
import type {CalendarState, CalendarStateHandlers} from './tabs/calendar/hooks';
import type {ModalVariant} from './types';

export type ActivityModalContextValue = {
    title: string;
    setTitle: (title: string) => void;
    previousActivity: PreviousActivity | null | undefined;
    setPreviousActivity: (activity: PreviousActivity) => void;
    currentActivityType: PickerSelectOption;
    handleSetActivityType: (activityType: PickerSelectOption) => void;
    selectedRelatedEntities: Option[];
    setSelectedRelatedEntities: (entities: Option[]) => void;
    potentialRelatedEntities: RelatedEntity[];
    potentialParticipants: Participant[];
    calendarState: CalendarState;
    calendarHandlers: CalendarStateHandlers;
    createActivityForModal: (input: ActivityCreateInput) => void;
    isCreatingActivity: boolean;
    createError: string | null | undefined;
    shouldSyncToCalendar: boolean;
    setShouldSyncToCalendar: (shouldSyncToCalendar: boolean) => void;
    isCalendarSyncEnabled: boolean;
    calendarSyncExternalService: ExternalService | null | undefined;
    primaryCalendarExportRule: string | null | undefined;
    isUserParticipating: boolean;
    createdFromLead: boolean;
    logNote: string;
    quillDelta: string;
    handleSetLogNote: (note: string) => void;
    logOnCreate: boolean;
};

const ActivityModalContext = React.createContext<ActivityModalContextValue | null | undefined>(
    null
);

type ProviderProps = {
    children: React.ReactNode;
    entityId: string;
    variant: ModalVariant;
    handleCreateActivity: (input: ActivityCreateInput) => void;
    isCreatingActivity: boolean;
    createError: string | null | undefined;
    calendarSync: CalendarSync | null | undefined;
    userId: string | null | undefined;
};

export function ActivityModalContextProvider(props: ProviderProps) {
    const [title, setTitle] = React.useState<string>('');
    const [previousActivity, setPreviousActivity] = React.useState<PreviousActivity | null>(null);
    const [currentActivityType, setCurrentActivityType] = React.useState<PickerSelectOption>({
        value: '',
        label: '',
    });
    const [selectedRelatedEntities, setSelectedRelatedEntities] = React.useState<Option[]>([]);

    const [potentialRelatedEntities, setPotentialRelatedEntities] = React.useState<RelatedEntity[]>(
        []
    );
    const [potentialParticipants, setPotentialParticipants] = React.useState<Participant[]>([]);
    const [shouldSyncToCalendar, setShouldSyncToCalendar] = React.useState<boolean>(
        getOrCreateLocalStorageShouldSyncToCalendar()
    );

    const [logNote, setLogNote] = React.useState<string>('');
    const [quillDelta, setQuillDelta] = React.useState<string>('');

    const participantsUpdatedRef = React.useRef<boolean>(false);

    const [calendarState, calendarHandlers] = useCalendarState({
        initialState: {
            agenda: '',
            location: '',
            startTime: 0,
            endTime: 0,
            isAllDay: false,
            isMultiDay: false,
            selectedParticipants: [],
            videoConferencingData: null,
        },
        onParticipantsChange: () => {
            participantsUpdatedRef.current = true;
        },
    });

    const urlParams = React.useMemo(() => {
        const hashWithQuery = window.location.hash;
        const queryStart = hashWithQuery.indexOf('?');
        const queryString = queryStart !== -1 ? hashWithQuery.substring(queryStart + 1) : '';
        const searchParams = new URLSearchParams(queryString);

        return {
            seededActivityTypeId: searchParams.get('activityTypeId') || null,
            seededDurationMinutes: searchParams.get('durationMinutes') || null,
            seededNote: searchParams.get('note') || null,
        };
    }, []);

    const {activityDefaults} = useGetActivityDefaults(props.entityId, props.variant === 'update', {
        activityTypeId: urlParams.seededActivityTypeId,
        durationMinutes: urlParams.seededDurationMinutes
            ? Number(urlParams.seededDurationMinutes)
            : null,
        note: urlParams.seededNote,
    });

    const hasRelatedEntities =
        selectedRelatedEntities.length > 0 || calendarState.selectedParticipants.length > 0;
    const shouldSkipPotentialEntities = !hasRelatedEntities || props.variant === 'update';

    const {relatedEntities, participants} = useGetPotentialActivityEntities(
        [
            ...selectedRelatedEntities.map((entity) => entity.id),
            ...calendarState.selectedParticipants.map((participant) => participant.id),
        ],
        shouldSkipPotentialEntities
    );

    React.useEffect(() => {
        if (activityDefaults) {
            setTitle(activityDefaults.name);
            setQuillDelta(activityDefaults.quillDelta || '');
            setLogNote(activityDefaults.logNote || '');
            setCurrentActivityType({
                value: activityDefaults.activityType.id,
                label: activityDefaults.activityType.name,
            });
            setSelectedRelatedEntities(
                activityDefaults.relatedEntities.map((entity) => ({
                    id: entity.id,
                    name: entity.name,
                    avatarUrl: entity.avatarUrl,
                    initials: entity.initials || undefined,
                    metaData: {
                        href: entity.htmlUrl,
                    },
                }))
            );
            calendarHandlers.setStartTime(activityDefaults.startTime);
            calendarHandlers.setEndTime(activityDefaults.endTime);
            calendarHandlers.setSelectedParticipants(
                activityDefaults.participants.map((participant) => ({
                    id: participant.id,
                    name: participant.name,
                    avatarUrl: participant.avatarUrl,
                    initials: participant.initials || undefined,
                    emailAddress: participant.primaryEmailAddress || '',
                    type: participant.type,
                    metaData: {
                        href: participant.htmlUrl,
                    },
                }))
            );
            calendarHandlers.setIsMultiDay(
                checkMultiDay(activityDefaults.startTime, activityDefaults.endTime)
            );
            if (activityDefaults.previousActivity) {
                setPreviousActivity(activityDefaults.previousActivity);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityDefaults]);

    React.useEffect(() => {
        if (relatedEntities) {
            setPotentialRelatedEntities(relatedEntities);
        }

        if (participants) {
            setPotentialParticipants(participants);
        }
    }, [relatedEntities, participants, props.userId]);

    const isUserParticipating =
        props.userId &&
        calendarState.selectedParticipants.some((participant) => participant.id === props.userId);
    React.useEffect(() => {
        // Only automatically disable sync if the user changed the participants
        if (!participantsUpdatedRef.current) {
            return;
        }

        if (isUserParticipating === false) {
            setShouldSyncToCalendar(false);
        }
    }, [isUserParticipating]);

    const handleSetActivityType = (activityType: PickerSelectOption) => {
        const currentType = currentActivityType.label;
        if (title === currentType) {
            setTitle(activityType.label);
        }
        setCurrentActivityType(activityType);
    };

    const handleSetLogNote = (note: string) => {
        setLogNote(note);
    };

    const isCalendarSyncEnabled = Boolean(props.calendarSync && props.calendarSync.isEnabled);
    const calendarSyncExternalService = props.calendarSync && props.calendarSync.externalService;
    const primaryCalendarExportRule =
        props.calendarSync &&
        props.calendarSync.primaryCalendar &&
        props.calendarSync.primaryCalendar.exportRule;
    const createdFromLead = Boolean(props.entityId && props.entityId.includes('leads'));

    const logOnCreate = props.variant === 'log';

    const contextValue: ActivityModalContextValue = {
        title,
        setTitle,
        previousActivity,
        setPreviousActivity,
        currentActivityType,
        handleSetActivityType,
        selectedRelatedEntities,
        setSelectedRelatedEntities,
        potentialRelatedEntities,
        potentialParticipants,
        calendarState,
        calendarHandlers,
        createActivityForModal: props.handleCreateActivity,
        isCreatingActivity: props.isCreatingActivity,
        createError: props.createError,
        shouldSyncToCalendar,
        setShouldSyncToCalendar,
        isCalendarSyncEnabled,
        calendarSyncExternalService,
        primaryCalendarExportRule,
        isUserParticipating: isUserParticipating || false,
        createdFromLead: createdFromLead,
        logNote,
        quillDelta,
        handleSetLogNote,
        logOnCreate,
    };

    return (
        <ActivityModalContext.Provider value={contextValue}>
            {props.children}
        </ActivityModalContext.Provider>
    );
}

export function mapContextToCreateInput(
    contextValue: ActivityModalContextValue
): ActivityCreateInput {
    const {
        title,
        currentActivityType,
        selectedRelatedEntities,
        calendarState,
        shouldSyncToCalendar,
        createdFromLead,
        previousActivity,
        logNote,
        logOnCreate,
    } = contextValue;
    const timeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;

    return {
        name: title,
        activityType: currentActivityType.value,
        relatedLeadIds: selectedRelatedEntities.map((entity) => entity.id),
        isFlagged: false,
        isForceSync: shouldSyncToCalendar,
        isLogged: logOnCreate,
        calendar: {
            agenda: calendarState.agenda,
            location: calendarState.location,
            startTime: calendarState.startTime,
            endTime: calendarState.endTime,
            allDay: calendarState.isAllDay,
            timeZone: timeZone !== undefined ? timeZone : null,
            participants: calendarState.selectedParticipants.map((participant) => participant.id),
            conferenceData: calendarState.videoConferencingData
                ? {
                      externalId: calendarState.videoConferencingData.meetingId,
                      externalUuid: calendarState.videoConferencingData.meetingUuid,
                      meetingUrl: calendarState.videoConferencingData.meetingUrl,
                      serviceType: calendarState.videoConferencingData.serviceType,
                  }
                : null,
        },
        createdFromLead: createdFromLead,
        previousActivity: previousActivity && previousActivity.id,
        logNote: logNote,
    };
}

export function useActivityModalContext(): ActivityModalContextValue {
    const context = React.useContext(ActivityModalContext);
    if (!context) {
        throw new Error('useActivityModalContext must be used within ActivityModalContextProvider');
    }

    return context;
}
